import React, { useState } from 'react';

const stamp = value => value ? new Date(value).toLocaleString() : 'Date unavailable';
const summary = text => {
  const compact = text.replace(/\s+/g, ' ').trim();
  return compact.length > 100 ? `${compact.slice(0, 100)}…` : compact;
};
const pageSize = 10;

export default function ProjectUpdates({ updates, onDownload }) {
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  // Updates are appended by the API; reverse a copy to show the newest first.
  const newest = [...updates].reverse();
  const selected = updates.find(entry => entry._id === selectedId);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(newest.length / pageSize) - 1));
  const visible = showAll ? newest.slice(currentPage * pageSize, (currentPage + 1) * pageSize) : newest.slice(0, 3);

  if (selected) return <div className="space-y-4">
    <button type="button" className="text-indigo-700 underline" onClick={() => setSelectedId(null)}>Back to {showAll ? 'all updates' : 'latest updates'}</button>
    <article className="rounded-lg border p-4 space-y-3" aria-label="Update details">
      <h5 className="text-lg font-semibold">Update details</h5>
      <p className="text-sm text-gray-600">{selected.authorName} · {stamp(selected.createdAt)}</p>
      <p className="whitespace-pre-wrap break-words">{selected.text}</p>
      {!!selected.attachments.length && <><h6 className="font-semibold">Attachments</h6><ul className="space-y-2">{selected.attachments.map(link => <li key={link._id || link.fileId || link.url}>
        {link.fileId ? <button type="button" className="text-indigo-700 underline break-words" onClick={() => onDownload(link)}>Download {link.kind}: {link.title}</button> : <a className="text-indigo-700 underline break-words" href={link.url} target="_blank" rel="noopener noreferrer">{link.kind === 'video' ? 'Video' : 'Document'}: {link.title}<span className="sr-only"> (opens in a new tab)</span></a>}
      </li>)}</ul></>}
    </article>
  </div>;

  return <div className="space-y-3">
    {!newest.length ? <p>No updates yet.</p> : <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">{showAll ? `All updates (${newest.length})` : `Latest ${visible.length} of ${newest.length} updates`}</p>
        {(showAll || newest.length > 3) && <button type="button" className="text-indigo-700 underline" onClick={() => { setShowAll(value => !value); setPage(0); }}>{showAll ? 'Show latest 3 updates' : `View all updates (${newest.length})`}</button>}
      </div>
      <div className="overflow-x-auto rounded-lg border shadow-sm">
        <table className="w-full text-left text-sm">
          <caption className="sr-only">{showAll ? 'All project updates' : 'Latest project updates'}</caption>
          <thead className="bg-blue-600 text-white"><tr>{['Update', 'Posted by', 'Date', 'Attachments', 'Action'].map(label => <th key={label} scope="col" className="px-4 py-3 font-medium">{label}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200">{visible.map(entry => <tr key={entry._id} className="bg-white hover:bg-gray-50">
            <th scope="row" className="px-4 py-3 font-medium"><span className="block w-48 truncate sm:w-64">{summary(entry.text)}</span></th>
            <td className="px-4 py-3">{entry.authorName}</td>
            <td className="px-4 py-3 whitespace-nowrap text-gray-600">{stamp(entry.createdAt)}</td>
            <td className="px-4 py-3">{entry.attachments.length}</td>
            <td className="px-4 py-3"><button type="button" className="whitespace-nowrap text-indigo-700 underline" aria-label={`View update: ${summary(entry.text)}`} onClick={() => setSelectedId(entry._id)}>View details</button></td>
          </tr>)}</tbody>
        </table>
      </div>
      {showAll && newest.length > pageSize && <div className="flex items-center justify-between gap-3 text-sm">
        <span>Page {currentPage + 1} of {Math.ceil(newest.length / pageSize)}</span>
        <div className="flex gap-3"><button type="button" className="rounded border px-3 py-2 disabled:opacity-40" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Previous updates</button><button type="button" className="rounded border px-3 py-2 disabled:opacity-40" disabled={(currentPage + 1) * pageSize >= newest.length} onClick={() => setPage(currentPage + 1)}>Next updates</button></div>
      </div>}
    </>}
  </div>;
}
